/**
 * SmartRoomPicker
 * -----------------------------------------------------------------------------
 * Searchable, filterable room selector for accommodation allocation.
 * Replaces the long dropdown. Keyboard-friendly, mobile-friendly, and enriched
 * with live occupancy indicators.
 *
 * Props:
 *   value              -> currently selected room_id (string)
 *   onChange(roomId, room?) -> callback when a room is picked
 *   requiredBeds       -> minimum beds required (default 1) to filter availability
 *   showOnlyAvailable  -> default true; when false, shows all rooms
 *   placeholder        -> button label when nothing selected
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, BedDouble, Building2, MapPin, Filter, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = 'accommodation.smart_room_picker.recent';

const stateColor = {
  available: 'bg-green-500',
  nearly_full: 'bg-yellow-500',
  full: 'bg-red-500',
  out_of_service: 'bg-gray-500',
};
const stateLabel = {
  available: 'Available',
  nearly_full: 'Nearly Full',
  full: 'Full',
  out_of_service: 'Out of Service',
};

function useRecentRooms() {
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const push = useCallback((room) => {
    if (!room?.id) return;
    setRecent((prev) => {
      const next = [{ id: room.id, room_number: room.room_number, building_name: room.building_name }, ...prev.filter((r) => r.id !== room.id)].slice(0, 6);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);
  return { recent, push };
}

const SmartRoomPicker = ({
  value,
  onChange,
  requiredBeds = 1,
  showOnlyAvailable = true,
  placeholder = 'Search rooms…',
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [buildingId, setBuildingId] = useState('all');
  const [availability, setAvailability] = useState(showOnlyAvailable ? 'available' : 'any');
  const [roomType, setRoomType] = useState('all');
  const [sort, setSort] = useState('building');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const { recent, push } = useRecentRooms();

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}` };

  // Buildings for the filter
  const { data: buildings = [] } = useQuery({
    queryKey: ['accommodation-buildings-picker'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/accommodation/buildings`, { headers });
      if (!r.ok) return [];
      return r.json();
    },
  });

  // Search results
  const searchParams = useMemo(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.append('q', q.trim());
    if (buildingId !== 'all') params.append('building_id', buildingId);
    if (roomType !== 'all') params.append('room_type', roomType);
    if (availability !== 'any') params.append('availability', availability);
    if (requiredBeds > 1) params.append('min_available', String(requiredBeds));
    if (sort) params.append('sort', sort);
    params.append('limit', '150');
    return params.toString();
  }, [q, buildingId, roomType, availability, requiredBeds, sort]);

  const { data, isFetching } = useQuery({
    queryKey: ['smart-room-search', searchParams],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/accommodation/rooms/search?${searchParams}`, { headers });
      if (!r.ok) return { rooms: [], count: 0 };
      return r.json();
    },
    enabled: open,
    keepPreviousData: true,
  });

  const rooms = data?.rooms || [];

  useEffect(() => {
    setActiveIdx(0);
  }, [searchParams]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Keyboard navigation
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(rooms.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = rooms[activeIdx];
      if (r) pick(r);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const pick = (r) => {
    if (r.availability_state === 'out_of_service') return;
    if (r.availability_state === 'full' && availability === 'available') return;
    push(r);
    onChange?.(r.id, r);
    setOpen(false);
  };

  // Selected label for the closed button
  const { data: selectedRoom } = useQuery({
    queryKey: ['selected-room', value],
    queryFn: async () => {
      if (!value) return null;
      const r = await fetch(`${API_URL}/api/admin/accommodation/rooms/${value}/occupancy`, { headers });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: !!value,
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left border rounded-md px-3 py-2 flex items-center justify-between hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="smart-room-picker-trigger"
      >
        <span className="flex items-center gap-2 text-sm">
          <BedDouble className="w-4 h-4 text-slate-500" />
          {value && selectedRoom ? (
            <span>
              <span className="font-medium">Room {selectedRoom.room_number}</span>
              <span className="text-slate-500 ml-2 text-xs">
                {selectedRoom.occupied}/{selectedRoom.capacity} occupied · {selectedRoom.available} available
              </span>
            </span>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
        </span>
        <Search className="w-4 h-4 text-slate-400" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-base">Find a room</DialogTitle>
          </DialogHeader>

          {/* Search bar */}
          <div className="px-4 pb-3 pt-1">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search room number, building, floor, room type, capacity…"
                className="pl-9 pr-9"
                data-testid="smart-room-picker-search"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <Select value={buildingId} onValueChange={setBuildingId}>
                <SelectTrigger className="h-8 w-auto text-xs">
                  <SelectValue placeholder="Building" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All buildings</SelectItem>
                  {buildings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={roomType} onValueChange={setRoomType}>
                <SelectTrigger className="h-8 w-auto text-xs">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any type</SelectItem>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="double">Double</SelectItem>
                  <SelectItem value="triple">Triple</SelectItem>
                  <SelectItem value="quad">Quad</SelectItem>
                  <SelectItem value="dorm">Dormitory</SelectItem>
                  <SelectItem value="suite">Suite</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                </SelectContent>
              </Select>

              <Select value={availability} onValueChange={setAvailability}>
                <SelectTrigger className="h-8 w-auto text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available only</SelectItem>
                  <SelectItem value="any">All rooms</SelectItem>
                  <SelectItem value="full">Full</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-8 w-auto text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="building">Sort: Building</SelectItem>
                  <SelectItem value="available">Sort: Most Available</SelectItem>
                  <SelectItem value="capacity">Sort: Largest</SelectItem>
                  <SelectItem value="room_number">Sort: Room #</SelectItem>
                </SelectContent>
              </Select>

              <span className="ml-auto text-xs text-slate-400">
                {isFetching ? 'Searching…' : `${rooms.length} result${rooms.length === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>

          {/* Recent */}
          {!q && recent.length > 0 && (
            <div className="px-4 pb-2">
              <div className="text-xs text-slate-500 mb-1">Recent</div>
              <div className="flex flex-wrap gap-1">
                {recent.map((r) => (
                  <Badge
                    key={r.id}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => {
                      onChange?.(r.id);
                      setOpen(false);
                    }}
                  >
                    Room {r.room_number}
                    {r.building_name ? <span className="text-slate-500 ml-1">· {r.building_name}</span> : null}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Result list */}
          <div ref={listRef} className="max-h-[420px] overflow-auto border-t" role="listbox" aria-label="Room search results">
            {rooms.length === 0 && !isFetching && (
              <div className="p-8 text-center text-sm text-slate-500">
                <BedDouble className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                No rooms match your search. Try clearing filters or changing your query.
              </div>
            )}
            <ul className="divide-y">
              {rooms.map((r, idx) => {
                const isActive = idx === activeIdx;
                const disabled = r.availability_state === 'out_of_service' || (availability === 'available' && r.availability_state === 'full');
                return (
                  <li
                    key={r.id}
                    role="option"
                    aria-selected={value === r.id}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => !disabled && pick(r)}
                    className={`px-4 py-3 flex items-center gap-3 cursor-pointer ${isActive ? 'bg-slate-50' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`smart-room-picker-item-${r.room_number}`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${stateColor[r.availability_state]}`} aria-label={stateLabel[r.availability_state]} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        Room {r.room_number}
                        <span className="text-slate-500 font-normal ml-2 text-xs">{r.room_type}</span>
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{r.building_name || '—'}</span>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />Floor {r.floor ?? '—'}</span>
                        <span>{r.capacity} beds</span>
                        <span>{r.occupied} occupied</span>
                        <span className={r.available === 0 ? 'text-red-600' : 'text-green-700'}>{r.available} available</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="px-4 py-2 border-t text-[11px] text-slate-500 flex items-center gap-3 bg-slate-50">
            <span><kbd className="px-1 border rounded">↑</kbd> <kbd className="px-1 border rounded">↓</kbd> navigate</span>
            <span><kbd className="px-1 border rounded">Enter</kbd> select</span>
            <span><kbd className="px-1 border rounded">Esc</kbd> close</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SmartRoomPicker;
